// FeatureCostEditor — Owner-only Tabelle der Feature-Preise (eve_feature_costs).
// Pro Zeile editierbare Credit-Zahl + Speichern-Button (PUT /admin/feature-costs/:feature).
// Self-contained: lädt selbst via useAuth-Token, hält eigenen Draft-State.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import {
  listFeatureCosts,
  updateFeatureCost,
  type FeatureCategory,
  type FeatureCostRow,
  type FeatureUnit,
} from '@/lib/admin-credits-api';

const CATEGORY_LABEL: Record<FeatureCategory, string> = {
  llm: 'KI-Modell',
  background: 'Hintergrund',
};

const UNIT_LABEL: Record<FeatureUnit, string> = {
  action: 'pro Aktion',
  day: 'pro Tag',
};

export function FeatureCostEditor() {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<FeatureCostRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await listFeatureCosts(accessToken);
      setRows(data);
      setDrafts(Object.fromEntries(data.map((r) => [r.feature, String(r.credits)])));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function save(row: FeatureCostRow) {
    if (!accessToken) return;
    const raw = drafts[row.feature] ?? '';
    const credits = Number.parseInt(raw, 10);
    if (!Number.isFinite(credits) || credits < 0) {
      setError(`${row.label}: Credits müssen ≥ 0 sein.`);
      return;
    }
    setSavingKey(row.feature);
    setError(null);
    try {
      const updated = await updateFeatureCost(accessToken, row.feature, {
        credits,
        unit: row.unit,
        category: row.category,
        label: row.label,
      });
      setRows((prev) => prev.map((r) => (r.feature === row.feature ? updated : r)));
      setDrafts((prev) => ({ ...prev, [row.feature]: String(updated.credits) }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <section className="glass-strong rounded-2xl p-5">
      <div className="mb-4">
        <h2 className="text-base font-semibold">Feature-Preise</h2>
        <p className="text-[12px] text-neutral-500">
          Credits pro Aktion bzw. pro Tag. Änderungen wirken sofort ohne Redeploy.
        </p>
      </div>

      {error ? (
        <div className="mb-3 rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="py-8 text-center text-[12px] text-neutral-500">Lade Feature-Preise…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-white/5 text-left text-[11px] uppercase tracking-wider text-neutral-500">
                <th className="pb-3 pr-4 font-medium">Feature</th>
                <th className="pb-3 px-4 font-medium">Kategorie</th>
                <th className="pb-3 px-4 w-28 text-center font-medium">Credits</th>
                <th className="pb-3 px-4 font-medium">Einheit</th>
                <th className="pb-3 pl-4 w-28 text-right font-medium"> </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const draft = drafts[row.feature] ?? '';
                const dirty = draft !== String(row.credits);
                return (
                  <tr key={row.feature} className="border-t border-white/5">
                    <td className="py-3 pr-4">
                      <div className="text-neutral-100">{row.label}</div>
                      <div className="font-mono text-[10px] text-neutral-500">{row.feature}</div>
                    </td>
                    <td className="px-4 text-[12px] text-neutral-400">
                      {CATEGORY_LABEL[row.category]}
                    </td>
                    <td className="px-4 text-center">
                      <input
                        type="number"
                        value={draft}
                        min={0}
                        step={1}
                        onChange={(e) =>
                          setDrafts((prev) => ({ ...prev, [row.feature]: e.target.value }))
                        }
                        className="w-20 rounded border border-white/10 bg-white/5 px-2 py-1 text-center text-sm tabular outline-none focus:border-amber-500/40"
                        aria-label={`Credits für ${row.label}`}
                      />
                    </td>
                    <td className="px-4 text-[12px] text-neutral-400">{UNIT_LABEL[row.unit]}</td>
                    <td className="pl-4 text-right">
                      <button
                        type="button"
                        onClick={() => void save(row)}
                        disabled={!dirty || savingKey === row.feature}
                        className="rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-1 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40"
                      >
                        {savingKey === row.feature ? 'Speichere…' : 'Speichern'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-neutral-500">
                    Keine Feature-Preise hinterlegt.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
